/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import Sha256 from 'cchome-shared/utils/Sha256';
import SearchUtils from '../src/containers/Search/SearchUtils';
import HomeAnalytics from '../src/containers/Home/Analytics/HomeAnalytics';
import {getEnvironment} from './Environment';

interface TargetBody {
	visitorId: string;
	countryCode: string;
	entityId?: string;
	mboxUrl: string;
	sdid: string;
	mcid: string;
	recommendationSource?: string;
}

class TargetUtils {
	/**
	 * Given a two letter locale, returns a unique page id for the current pathname.
	 *
	 * Assumes: window is defined
	 *
	 * @param {string} language ex: en, fr
	 * @param {string} articleSlug ex: en, fr
	 * @returns {string}
	 */
	static getPageId(language: string, articleSlug?: string) {
		let pageId;
		if (language && articleSlug) {
			pageId = `${language}_${articleSlug}`;
		}
		else {
			pageId = window.location.pathname;
		}
		return pageId;
	}

	/**
	 * Returns a unique entity id for a page. Must be reproducable for the same page
	 * meaing /discover/article/foo and /discover/article/foo?a=b should have the same
	 * entity id. If you refresh /discover/article/foo, returns same entity id. Target
	 * uses the entity id from a page metadata to record analytics on that page.
	 *
	 * @param {string} language  ex: en, fr
	 * @returns {string}
	 */
	static getEntityId(articleSlug?: string) {
		// Using english since source only knows english version. server localizes them
		return new Sha256().generateIgnoreCase(TargetUtils.getPageId('en', articleSlug));
	}

	/**
	 * Returns the post body information required to call suggestedarticles
	 * POST on UCS.
	 * @param {string} entityId
	 * @param {string} countryCode ex: us
	 * @returns {object}
	 */
	static getTargetInfo(entityId?: string, countryCode?: string) {
		const analytics = new HomeAnalytics();
		analytics.ingest.initializeDefaultData();

		// TODO: maybe make this a method to make this easier to get to?
		let visitorGuid;
		if (analytics.ingest.eventsObject) {
			visitorGuid = analytics.ingest.eventsObject.visitor_guid;
		}

		if (!window.Visitor || visitorGuid === undefined) {
			return null;
		}

		// Name of place to dump all of the Adobe Analytics data
		const targetInstance = '9E1005A551ED61CA0A490D45@AdobeOrg';

		const visitor = window.Visitor.getInstance(targetInstance);
		const mcid = visitor.getMarketingCloudVisitorID();
		// TODO: need to add getSupplementalDataID to react-ingest so I don't have to do this
		let sdid = visitor._supplementalDataIDLast;
		if (sdid === '') {
			sdid = visitor.getSupplementalDataID();
		}

		const targetBody: TargetBody = {
			visitorId: SearchUtils.getIMSData().userID,
			countryCode: countryCode || 'us',
			entityId,
			mboxUrl: window && window.location ? window.location.href : '',
			sdid,
			mcid,
		};

		const buckets = ['target', 'lda', 'control'];
		const bucketIndex = window.cchome.util.getBucket('recommendation_cards');
		if (bucketIndex >= 0 && bucketIndex <= 2) {
			targetBody.recommendationSource = buckets[Math.max(0, bucketIndex - 1)];
		}

		if (window.cchome.util.getBucket('lda-recommendations') === 1) {
			targetBody.recommendationSource = 'lda';
		}
		else if (window.cchome.util.getBucket('target-recommendations') === 1) {
			targetBody.recommendationSource = 'target';
		}
		return targetBody;
	}

	/**
	 * Adds the entity id to page's header. If an entity id already exists, updates it.
	 * Target uses the entity id from a page metadata to record analytics on that page.
	 * @param {string} language  ex: en, fr
	 */
	static addEntityId(language, entityId) {
		// change place
		if (document.getElementsByName('entity_id').length > 0) {
			document.getElementsByName('entity_id')[0].setAttribute('content', entityId);
		}
		else {
			const metaTag = document.createElement('meta');
			metaTag.name = 'entity_id';
			metaTag.content = entityId;
			document.getElementsByTagName('head')[0].appendChild(metaTag);
		}
	}
}

export default TargetUtils;
